// The vendor lifecycle, as the server describes it.
//
// Nothing here decides what is allowed. The transition table lives once, in
// packages/core/domain, and this client asks the API what may be done to a
// vendor and posts to whatever it is told. A console that kept its own copy
// of the rules would drift from the ones the API enforces, and the person
// pressing the button would be the one who found out.

import { can, request, type Capability, type VendorRequestStanding } from './api'

// The capability each move's route declares, mirroring
// packages/core/domain.MoveCapability. The API already leaves out the moves a
// caller cannot make (release 3.1); this is the same rule once more at the
// button, so a console talking to an older API still does not offer a move
// whose POST would answer 403.
export const MOVE_NEEDS: Record<string, Capability> = {
  submit: 'vendor.edit',
  return: 'vendor.edit',
  suspend: 'vendor.edit',
  approve: 'vendor.approve',
  reject: 'vendor.approve',
  resume: 'vendor.approve',
  offboard: 'offboarding.run',
  offboarded: 'offboarding.run',
}

export function mayMove(action: string): boolean {
  const need = MOVE_NEEDS[action]
  return need !== undefined && can(need)
}

// The persona spec's id for each move's control (X0 test hooks).
export const MOVE_ACTION_ID: Record<string, string> = {
  submit: 'tprm.vendor,vendor-record.a02',
  return: 'tprm.vendor,vendor-record.a03',
  suspend: 'tprm.vendor,vendor-record.a04',
  approve: 'tprm.vendor,vendor-record.a05',
  reject: 'tprm.vendor,vendor-record.a06',
  resume: 'tprm.vendor,vendor-record.a07',
  offboard: 'tprm.exit-workspace.a01',
  offboarded: 'tprm.exit-workspace.a02',
}

export type LifecycleMove = {
  action: string
  to: string
  requiresApproval: boolean
  requiresReason: boolean
  why: string
  // Set when this caller specifically may not make the move, with the reason,
  // so the button is greyed rather than pressed into a 409.
  blockedBecause?: string
}

export type VendorLifecycle = {
  id: string
  status: string
  lastActor: string
  approvedBy: string | null
  statusSince: string | null
  moves: LifecycleMove[]
  // The request step (release 3.3). Absent from an API older than it.
  request?: VendorRequestStanding
}

export function vendorLifecycle(id: string): Promise<VendorLifecycle> {
  return request<VendorLifecycle>('/v1/vendors/' + encodeURIComponent(id) + '/lifecycle')
}

// baselineOverride is submit's only (release 3.3): why the vendor goes forward
// without a scored due diligence on its tier's questionnaire.
export function vendorTransition(
  id: string,
  action: string,
  reason: string,
  baselineOverride = '',
): Promise<{ status: string; from: string; eventId: string }> {
  return request('/v1/vendors/' + encodeURIComponent(id) + '/' + encodeURIComponent(action), {
    method: 'POST',
    body: JSON.stringify(baselineOverride ? { reason, baselineOverride } : { reason }),
  })
}
